import React from 'react';
import {withSystem, Plugins, Component, Atom} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {UsysUtilityPageConstraint} from '../utils';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  TListOfElement,
  TRecord,
  TEnum,
  TTypeConstructor,
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EList,
  ELiteral,
} from '@packages/systems/wfdl-ast';

import {
  ERROR_STATE,
  LOGIN_UI_ERROR_CODES,
  logInErrorStates,
} from '@packages/systems/users/constants';
import {Map} from 'immutable';
import {
  EvaluatedNodeData,
  getChildren,
  getString,
  getInPojo,
} from '@packages/utilities/data-utils';

const UserLogInFormWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, data, renderChild}) => {
    const formState = getString(getInPojo(data, ['temp', 'state'])) ?? 'normal';
    const redirectPageId = getString(getInPojo(data, ['redirectPageId']));

    const context = Map({formState, redirectPageId});

    return (
      <div {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, context)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  constraints: {
    descendants: [
      {
        is: 'UserLogInForm',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormErrorState',
        rule: 'ExactlyOne',
      },
    ],
    page: [UsysUtilityPageConstraint],
  },
  displayName: 'Log In Form Block',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  render: UserLogInFormWrapper,
  showEmpty: true,
  fields: {
    temp: TRecord({
      state: withSystem(
        {label: 'State', defaultValue: 'normal', isFullWidth: true},
        TEnum({
          normal: 'Normal',
          error: 'Error',
        })
      ),
    }),
    redirectPageId: withSystem(
      {
        label: 'Redirect fallback',
        isFullWidth: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('LinkPageSelectorType'))
      )
    ),
    children: TListOfElement,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          position: 'relative',
          marginBottom: px(0),
          paddingLeft: px(20),
          paddingRight: px(20),
        },
      },
    },
  },
});

export default config;

export const UserLogInFormPreset = EElement({
  id: 'User Log In Form Wrapper',
  type: qualify(Plugins.name('Users'), Component.name('UserLogInFormWrapper')),
  data: ERecord({
    temp: ERecord({
      state: EEnum('normal'),
    }),
    redirectPageId: EText(''),
    children: EList([
      EElement({
        id: 'User Log In Form',
        type: qualify(Plugins.name('Users'), Component.name('UserLogInForm')),
        data: ERecord({
          children: EList([
            EElement({
              id: 'User Form Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap Heading',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Log in'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Block Label',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormBlockLabel')
              ),
              data: ERecord({
                attr: ERecord({
                  for: EText('Email'),
                }),
                users: ERecord({
                  field: EText(''),
                }),
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Email'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Email Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormEmailInput')
              ),
              data: ERecord({
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  placeholder: EText(''),
                  disabled: EBoolean(false),
                  name: EText('Email'),
                  id: EText('wf-log-in-email'),
                }),
              }),
            }),
            EElement({
              id: 'User Form Block Label 2',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormBlockLabel')
              ),
              data: ERecord({
                attr: ERecord({
                  for: EText('Password'),
                }),
                users: ERecord({
                  field: EText(''),
                }),
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 3',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Password'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Password Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormPasswordInput')
              ),
              data: ERecord({
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  placeholder: EText(''),
                  disabled: EBoolean(false),
                  name: EText('Password'),
                  id: EText('wf-log-in-password'),
                }),
              }),
            }),
            EElement({
              id: 'User Form Button',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormButton')
              ),
              data: ERecord({
                attr: ERecord({
                  type: EText('submit'),
                  value: EText('Log In'),
                  'data-wait': EText('Please wait...'),
                }),
              }),
            }),
            EElement({
              id: 'User Form Footer',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormFooter')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap Span',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Span')
                    ),
                    data: ERecord({
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String 4',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText("Don't have an account?"),
                        }),
                      ]),
                    }),
                  }),
                  EElement({
                    id: 'User Form Page Wrap Link',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Link')
                    ),
                    data: ERecord({
                      button: EBoolean(false),
                      block: EText('inline'),
                      eventIds: EList([]),
                      link: ELiteral({
                        name: qualify(Plugins.name('Basic'), typeName('Link')),
                        value: {
                          mode: 'external',
                          url: '/sign-up',
                        },
                      }),
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String 5',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Sign Up'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Log in Error State',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormErrorState')
        ),
        data: ERecord({
          users: ERecord({
            type: EText(ERROR_STATE.LOGIN),
          }),
          children: EList([
            EElement({
              id: 'User Log in Error Msg',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserErrorMsg')
              ),
              data: ERecord({
                users: ERecord({
                  type: EText(ERROR_STATE.LOGIN),
                }),
                temp: ERecord({
                  index: ENumber(0),
                }),
                errors: EList([
                  ERecord({
                    id: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      logInErrorStates[LOGIN_UI_ERROR_CODES.GENERAL_ERROR].id
                    ),
                    name: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      logInErrorStates[LOGIN_UI_ERROR_CODES.GENERAL_ERROR].name
                    ),
                    copy: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      logInErrorStates[LOGIN_UI_ERROR_CODES.GENERAL_ERROR].copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      logInErrorStates[
                        LOGIN_UI_ERROR_CODES.INVALID_EMAIL_OR_PASSWORD
                      ].id
                    ),
                    name: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      logInErrorStates[
                        LOGIN_UI_ERROR_CODES.INVALID_EMAIL_OR_PASSWORD
                      ].name
                    ),
                    copy: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      logInErrorStates[
                        LOGIN_UI_ERROR_CODES.INVALID_EMAIL_OR_PASSWORD
                      ].copy
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    ]),
  }),
});
